// SD 回放跟不上：建議改錄影畫質（plan SPEC #device-app-sd-slow-tier，Eddy 2026-10-21 選單：不雙錄，回放跟不上時提示、
// 一鍵改 `kinio_recording.quality_tier`，現有錄影照舊用「高」播；firmware#666）。
//   · SlowPlaybackJudge：播放器實測的卡頓——第一格畫面之後、1× 播放時停 ≥ 1 秒算一次，60 秒內 2 次 = 跟不上；
//     開始播放 / 拖曳 / 換清單後 3 秒內開始的停頓、倍速 / 暫停 / 背景期間、以錯誤收場的都不算。
//   · slowTierEligible：獨立攝影機自己 SD 卡的本地回放 + 宣告標準檔 + 目前是高；能不能改看設定投影的 can_write（同設定表單）。
//   · slowHintSuppressed：× = 這台 24 小時內不出；〔不再提示〕= 以後都不出（只存這個瀏覽器，key = 帳號 + AID）。
// 判斷規則與 app/home-base 三平台同一套（web lib/kinio/slowTierHint.ts）；共用案例 lib/testdata/kinio-slow-tier-hint.json
// 是 app/home-base docs/testdata 那份的複本（改規則兩邊一起改）。

import { qualityTierValues } from './recordingQuality';

export const SLOW_STALL_MIN_MS = 1_000;
export const SLOW_WINDOW_MS = 60_000;
export const SLOW_STALLS = 2;
export const SLOW_GRACE_MS = 3_000;
export const SLOW_DISMISS_MS = 24 * 60 * 60_000;
/** 〔改成標準〕之後等設備回報多久；過了 = 「已送出」（desired 留著，設備連上後套用）。 */
export const SLOW_APPLY_WAIT_MS = 15_000;
/** 「已改成標準」「已送出」顯示多久後收掉。 */
export const SLOW_DONE_SHOW_MS = 5_000;

export class SlowPlaybackJudge {
  private started = false;
  private windowStart = 0;
  private count = 0;
  private stallAt: number | null = null;
  private graceUntil = 0;
  private eligible = true;

  /** 第一格畫面（同一個起點只認第一次）。 */
  firstFrame(now: number): void {
    if (this.started) return;
    this.started = true;
    this.windowStart = now;
    this.count = 0;
  }

  /** 開始播放、拖曳、點時間軸、換下一份清單：之後 SLOW_GRACE_MS 內開始的停頓不算。 */
  grace(now: number): void {
    this.graceUntil = now + SLOW_GRACE_MS;
    this.stallAt = null;
  }

  /** 1× 而且沒暫停、在前景。變成 false 時進行中的停頓作廢。 */
  setEligible(ok: boolean): void {
    this.eligible = ok;
    if (!ok) this.stallAt = null;
  }

  /** 播放出錯：進行中的停頓作廢。 */
  cancel(): void {
    this.stallAt = null;
  }

  /** 換起點重新播：全部重算（等下一個第一格畫面）。 */
  restart(): void {
    this.started = false;
    this.count = 0;
    this.stallAt = null;
    this.graceUntil = 0;
  }

  stall(now: number): void {
    if (!this.started || !this.eligible || now < this.graceUntil || this.stallAt !== null) return;
    this.stallAt = now;
  }

  /** 恢復播放；回 true = 這次判定跟不上。 */
  resume(now: number): boolean {
    const at = this.stallAt;
    this.stallAt = null;
    if (at === null || now - at < SLOW_STALL_MIN_MS) return false;
    if (at - this.windowStart > SLOW_WINDOW_MS) {
      this.windowStart = at;
      this.count = 1;
    } else this.count += 1;
    return this.count >= SLOW_STALLS;
  }
}

/** 設定投影裡的錄影畫質列（value / can_write）+ thing.capabilities（qualityTierValues）→ 這台能不能出提示、能不能改。 */
export function slowTierEligible(
  standaloneLocal: boolean,
  item: { value: unknown; can_write: boolean } | null | undefined,
  capabilities: unknown,
): { eligible: boolean; canWrite: boolean } {
  const no = { eligible: false, canWrite: false };
  if (!standaloneLocal || !item) return no;
  const v = item.value;
  if (!(v === 0 || v === '0' || v === 'high')) return no;
  const vals = qualityTierValues(capabilities);
  if (vals && !vals.has('1')) return no;
  return { eligible: true, canWrite: item.can_write === true };
}

export function slowHintSuppressed(dismissedAt: number | null, never: boolean, now: number): boolean {
  if (never) return true;
  return dismissedAt !== null && now - dismissedAt < SLOW_DISMISS_MS;
}

// ---- 本機記住（localStorage；讀寫失敗 = 沒記，照常判斷） ----

const keyOf = (uid: string, aid: string) => `kinio.slowTierHint.${uid}.${aid}`;

type Stored = { dismissedAt?: number; never?: boolean };

function readStored(uid: string, aid: string): Stored {
  try {
    const raw = localStorage.getItem(keyOf(uid, aid));
    const v = raw ? (JSON.parse(raw) as Stored) : {};
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

function writeStored(uid: string, aid: string, v: Stored): void {
  try {
    localStorage.setItem(keyOf(uid, aid), JSON.stringify(v));
  } catch {
    /* 私密模式 / 儲存滿：這次不記 */
  }
}

export function slowHintSuppressedFor(uid: string, aid: string, now: number): boolean {
  const s = readStored(uid, aid);
  return slowHintSuppressed(typeof s.dismissedAt === 'number' ? s.dismissedAt : null, s.never === true, now);
}

export function slowHintDismiss(uid: string, aid: string, now: number): void {
  writeStored(uid, aid, { ...readStored(uid, aid), dismissedAt: now });
}

export function slowHintNever(uid: string, aid: string): void {
  writeStored(uid, aid, { ...readStored(uid, aid), never: true });
}
